import React, { useState, useEffect, useRef } from "react";
import { CSVLink } from "react-csv";
import { connect } from "react-redux";

import {
  emptyString,
  loginConstants,
  GET_RESPONSE_TYPE,
  managerPerformanceStoresName,
  managerPerformanceExportHeaders,
  performanceScreensConstants,
} from "../../constants";
import {
  AVERAGE_RATING_RESPONSE_TYPE,
  starRatingGuestSentimentResponse,
} from "../../../admin-screens/constants";
import {
  modalSize,
  PERFORMANCE_DEFAULT_SORTING,
  PERFORMANCE_FILTER_DEFAULT_MIN_STAR,
  PERFORMANCE_FILTER_INITIAL_MAX_STAR,
  PERFORMANCE_EXPORT_CSV_DELAY_MS,
} from "../../../../constants";
import { getItemFromStorage } from "../../../../system";
import {
  fileName,
  isInclude,
  calculateChange,
  escapeSearchQuery,
  convertToDateToUnix,
  updatedColumnHeaders,
  convertFromDateToUnix,
  datePreferenceUtility,
  useRatingCheckboxGroup,
  guestSentimentPaginatedPostRequestBody,
  getConfirmationModal,
  UTCToDateFormat,
  timestampToDateTimeFormat,
  handleAsyncAction,
} from "../../../../shared";
import { managerPerformanceActions } from "../../redux/actions";
import {
  brandPerformanceActions,
  individualStoreProfileActions,
} from "../../../shared-screens/redux/actions";

import Performance from "./performance";
import FilterComponent from "./filterComponent";
import useModalDisplay from "../../../../shared/components/hooks/useModalDisplay/useModalDisplay";
import {
  buildSurveyCompletionDateFilterQuery,
  buildDatedPerformanceRequestPayload,
  mapStoresPerformanceExportRecords,
} from "../../../shared-screens/views/shared/performanceQueryUtility";

const { GUEST_SENTIMENTS, GUEST_TRANSACTION } = performanceScreensConstants;

const ManagerPerformance = (props) => {
  const {
    managerPerformanceReducer: {
      allItemsForExport,
      managerResponsesData,
      allManagerResponseData,
      managerResponseDataPagination,
      storesAvgSentimentAndTransactionRatingData,
    },
    loggedUser,
    frRoute,
    resetAllExportItems,
    getRecordPaginationResponses,
    getAllManagerResponsesItemsForExport,
    getAverageRatingResponses,
    exportDetailCustomerSurveyData,
  } = props;

  const csvLinkRef = useRef();
  const [exportData, setExportData] = useState([]);
  const [exportHeaders, setExportHeaders] = useState([]);
  const [sorting, setSorting] = useState(PERFORMANCE_DEFAULT_SORTING);
  const [selectedOption, setSelectedOption] = useState([]);
  const [starValue, setStarValue] = useState([]);
  const [maxStar, setMaxStar] = useState(PERFORMANCE_FILTER_INITIAL_MAX_STAR);
  const [minStar, setMinStar] = useState(PERFORMANCE_FILTER_DEFAULT_MIN_STAR);
  const [dayCounts, setDayCounts] = useState(emptyString);
  const [queryString, setQueryString] = useState(emptyString);
  const [searchString, setSearchString] = useState(emptyString);
  const [dateState, setDateState] = useState(datePreferenceUtility());

  const [showModal, modalElement, setComponent, closeModal] = useModalDisplay();

  const brandId = loggedUser.brandId;

  const createDateFrom = convertFromDateToUnix(dateState.startDate);
  const createDateTo = convertToDateToUnix(dateState.endDate);

  const { handleRatingOptionChange, selectedRatingOption } =
    useRatingCheckboxGroup(selectedOption, setStarValue, setSelectedOption);

  // set pagination details with request body
  const getPaginatedPostRequestBody = (pageNumber, sortBy, size) => {
    const averageSentiments = {
      min: minStar,
      max: maxStar,
    };
    const requestBody = guestSentimentPaginatedPostRequestBody({
      pagination: managerResponseDataPagination,
      pageNumber: pageNumber,
      sortBy: sortBy || sorting,
      filterQuery: queryString,
      averageSentiments: averageSentiments,
      isFrRoute: frRoute,
      size: size,
      isGuestSentimentFilter: selectedRatingOption === GUEST_SENTIMENTS,
    });
    if (sortBy) {
      setSorting(sortBy);
    }
    return { ...requestBody, search: escapeSearchQuery(searchString) };
  };

  // get table data with pagination
  const getData = (pageNumber, sortBy, size) => {
    const requestBody = getPaginatedPostRequestBody(pageNumber, sortBy, size);
    const managerId = getItemFromStorage(loginConstants.MANAGER_ID);

    //Avoid duplicate calls
    //If the selected checkbox option matches the sentiments or transaction filter query in the payload,
    //or if neither sentiments nor transaction filter is present, proceed with data retrieval.
    if (
      (isInclude(requestBody.filter, GUEST_SENTIMENTS) &&
        selectedRatingOption === GUEST_SENTIMENTS) ||
      (isInclude(requestBody.filter, GUEST_TRANSACTION) &&
        selectedRatingOption === GUEST_TRANSACTION) ||
      !(
        isInclude(requestBody.filter, GUEST_TRANSACTION) ||
        isInclude(requestBody.filter, GUEST_SENTIMENTS)
      )
    ) {
      const { GET_MANAGER_USER_RESPONSES } = GET_RESPONSE_TYPE;
      getRecordPaginationResponses(
        buildDatedPerformanceRequestPayload({
          createDateFrom,
          createDateTo,
          id: managerId,
          type: GET_MANAGER_USER_RESPONSES,
          requestBody,
        }),
      );

      const { GET_MANAGER_AVERAGE_RATING_RESPONSES } =
        AVERAGE_RATING_RESPONSE_TYPE;

      getAverageRatingResponses(
        buildDatedPerformanceRequestPayload({
          createDateFrom,
          createDateTo,
          id: managerId,
          type: GET_MANAGER_AVERAGE_RATING_RESPONSES,
          requestBody,
        }),
      );
    }
  };

  useEffect(() => {
    props.resetRestaurantProfile();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (queryString || searchString) {
      getData(0, null, managerResponseDataPagination.size);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchString, queryString, selectedRatingOption]);

  useEffect(() => {
    setQueryString(
      buildSurveyCompletionDateFilterQuery({
        createDateFrom,
        createDateTo,
        starValue,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOption, createDateTo, createDateFrom, starValue]);

  //Used to export CSV
  const exportCSV = () => {
    setTimeout(() => {
      csvLinkRef.current?.link.click();
    }, PERFORMANCE_EXPORT_CSV_DELAY_MS);
  };

  //Get all data for export
  const getExportData = () => {
    let headers;
    let requestId;
    let requestBody;
    const averageSentiments = {
      min: minStar,
      max: maxStar,
    };
    //Define common dataToSend array
    const dataToSend = {
      pagination: {
        size: managerResponseDataPagination.totalElements,
      },
      pageNumber: 0,
      sortBy: sorting,
      filterQuery: queryString,
      averageSentiments,
      isFrRoute: frRoute,
      isGuestSentimentFilter: selectedRatingOption === GUEST_SENTIMENTS,
    };

    //Determine requestId based on the type
    headers = managerPerformanceExportHeaders;
    requestId = getItemFromStorage(loginConstants.MANAGER_ID);

    //Create the requestBody object
    requestBody = guestSentimentPaginatedPostRequestBody(dataToSend);
    requestBody.search = searchString;

    getAllManagerResponsesItemsForExport(
      createDateFrom,
      createDateTo,
      requestId,
      requestBody,
    );

    setExportHeaders(updatedColumnHeaders(headers, dayCounts));
  };

  const handleConfirmCustomerSurveyData = () => {
    handleAsyncAction(
      exportDetailCustomerSurveyData(
        timestampToDateTimeFormat(createDateFrom),
        timestampToDateTimeFormat(createDateTo),
        brandId,
      ),
      { onSuccess: closeModal },
    );
  };

  // Open confirmationModal for export customer survey data
  const openExportCustomerDataConfirmationModal = () => {
    setComponent(
      getConfirmationModal({
        ModalSize: modalSize.MODAL_SIZE_MEDIUM,
        Title: "Confirmation",
        onClose: closeModal,
        modalBodyCssClassName: "text-center",
        selectedDateRange: `${UTCToDateFormat(
          createDateFrom,
        )} - ${UTCToDateFormat(createDateTo)}`,
        onConfirm: handleConfirmCustomerSurveyData,
        message:
          "Please confirm that you would like to export survey data for the following dates:",
        selectedDateRangeMessage:
          "Date ranges can be adjusted using the Selected Date parameters on the performance page",
      }),
    );
  };

  useEffect(() => {
    if (allItemsForExport.length) {
      setExportData(
        mapStoresPerformanceExportRecords(allItemsForExport, calculateChange),
      );
      exportCSV();
      resetAllExportItems();
    }
    // eslint-disable-next-line
  }, [allItemsForExport]);

  return (
    <>
      {showModal && modalElement}
      <FilterComponent
        dateState={dateState}
        setMaxStar={setMaxStar}
        setMinStar={setMinStar}
        setStarValue={setStarValue}
        setDateState={setDateState}
        setDayCounts={setDayCounts}
        setSelectedOption={setSelectedOption}
        selectedRatingOption={selectedRatingOption}
        handleRatingOptionChange={handleRatingOptionChange}
      />
      <Performance
        getData={getData}
        sorting={sorting}
        dayCounts={dayCounts}
        searchString={searchString}
        setSearchString={setSearchString}
        selectedOption={selectedOption}
        userResponsesData={managerResponsesData}
        totalRestaurants={allManagerResponseData.totalItems}
        totalResponses={allManagerResponseData.totalResponses}
        userResponseDataPagination={managerResponseDataPagination}
        starRatingGuestSentimentResponse={starRatingGuestSentimentResponse}
        exportFunc={() => getExportData()}
        avgSentimentAndTransactionRatingData={
          storesAvgSentimentAndTransactionRatingData
        }
        openExportCustomerDataConfirmationModal={
          openExportCustomerDataConfirmationModal
        }
      />
      <CSVLink
        target="_blank"
        ref={csvLinkRef}
        className="hidden"
        headers={exportHeaders}
        data={exportData}
        filename={`${fileName(
          managerPerformanceStoresName,
          createDateFrom,
          createDateTo,
        )}.csv`}
      />
    </>
  );
};

const mapStateToProps = (state) => ({
  managerPerformanceReducer: state.managerPerformanceReducer,
  loggedUser: state.authentication.user,
});

const mapDispatchToProps = (dispatch) => ({
  getAllManagerResponsesItemsForExport: (
    createDateFrom,
    createDateTo,
    managerId,
    body,
  ) =>
    dispatch(
      managerPerformanceActions.getAllManagerResponsesItemsForExport(
        createDateFrom,
        createDateTo,
        managerId,
        body,
      ),
    ),

  resetRestaurantProfile: () =>
    dispatch(individualStoreProfileActions.resetRestaurantProfile()),
  resetAllExportItems: () =>
    dispatch(managerPerformanceActions.resetAllExportItems()),
  getAverageRatingResponses: (payload) =>
    dispatch(brandPerformanceActions.getAverageRatingResponses(payload)),
  getRecordPaginationResponses: (payload) =>
    dispatch(brandPerformanceActions.getRecordPaginationResponses(payload)),
  exportDetailCustomerSurveyData: (createDateFrom, createDateTo, brandId) =>
    dispatch(
      brandPerformanceActions.exportDetailCustomerSurveyData(
        createDateFrom,
        createDateTo,
        brandId,
      ),
    ),
});

export default connect(mapStateToProps, mapDispatchToProps)(ManagerPerformance);
